(() => {
  const strings = window.ClevenneStrings || {};
  const text = (key, fallback) => strings[key] || fallback;

  const ready = (fn) => document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', fn, { once: true })
    : fn();

  ready(() => {
    const initializeStoryReveals = (root = document) => {
      const mediaItems = root.querySelectorAll('[data-clevenne-story-media]:not([data-clevenne-story-reveal-bound])');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      mediaItems.forEach((media) => {
        media.setAttribute('data-clevenne-story-reveal-bound', '');
        if (reducedMotion || !('IntersectionObserver' in window)) {
          media.classList.add('is-visible');
          return;
        }
        media.classList.add('is-reveal-ready');
        const observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          media.classList.add('is-visible');
          observer.disconnect();
        }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
        observer.observe(media);
      });
    };

    initializeStoryReveals();
    document.addEventListener('shopify:section:load', (event) => initializeStoryReveals(event.target));

    document.querySelectorAll('.header__inline-menu header-menu > details').forEach((details) => {
      const summary = details.querySelector(':scope > summary');
      let closeTimer;
      const open = () => {
        window.clearTimeout(closeTimer);
        details.setAttribute('open', '');
        summary?.setAttribute('aria-expanded', 'true');
      };
      const close = () => {
        closeTimer = window.setTimeout(() => {
          details.removeAttribute('open');
          summary?.setAttribute('aria-expanded', 'false');
        }, 120);
      };
      details.addEventListener('mouseenter', open);
      details.addEventListener('mouseleave', close);
      details.addEventListener('focusin', open);
      details.addEventListener('focusout', (event) => {
        if (!details.contains(event.relatedTarget)) close();
      });
    });

    const bestSellerSlider = document.querySelector('[id$="__best_sellers"] ul.slider');
    if (bestSellerSlider) {
      bestSellerSlider.scrollLeft = 0;
      window.setTimeout(() => { bestSellerSlider.scrollLeft = 0; }, 250);
      window.setTimeout(() => { bestSellerSlider.scrollLeft = 0; }, 750);
    }
    document.querySelectorAll('[id$="__best_sellers"] .card__inner img').forEach((image) => {
      image.sizes = '(max-width: 699px) 74vw, (max-width: 999px) 38vw, calc((100vw - 96px) / 5 - (24px / 5 * 4))';
    });
    const viewAll = document.querySelector('[id$="__best_sellers"] .collection__view-all a');
    if (viewAll) viewAll.textContent = text('viewAll', 'View all');
    const initializeBlogCarousels = (root = document) => {
      root.querySelectorAll('[data-clevenne-blog]:not([data-clevenne-blog-bound])').forEach((section) => {
        const rail = section.querySelector('[data-clevenne-blog-rail]');
        const cards = [...section.querySelectorAll('[data-clevenne-blog-card]')];
        const dots = [...section.querySelectorAll('[data-clevenne-blog-dot]')];
        const previous = section.querySelector('[data-clevenne-blog-prev]');
        const next = section.querySelector('[data-clevenne-blog-next]');
        if (!rail || cards.length < 2) return;

        section.setAttribute('data-clevenne-blog-bound', '');
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        let activeIndex = 0;
        let autoplay;
        let scrollFrame;

        const railPadding = () => parseFloat(window.getComputedStyle(rail).paddingInlineStart) || 0;
        const cardStep = () => cards[1].offsetLeft - cards[0].offsetLeft;
        const setMediaHeight = () => section.style.setProperty('--clevenne-blog-media-height', `${cards[0].offsetWidth}px`);
        const syncControls = (index) => {
          activeIndex = Math.max(0, Math.min(cards.length - 1, index));
          dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', String(dotIndex === activeIndex)));
          if (previous) previous.disabled = activeIndex === 0;
          if (next) next.disabled = activeIndex === cards.length - 1;
        };
        const scrollToCard = (index, behavior = 'smooth') => {
          const destination = index >= cards.length ? 0 : Math.max(0, index);
          rail.scrollTo({ left: cards[destination].offsetLeft - railPadding(), behavior });
          syncControls(destination);
        };
        const restartAutoplay = () => {
          window.clearInterval(autoplay);
          if (reducedMotion) return;
          autoplay = window.setInterval(() => scrollToCard(activeIndex + 1), 4000);
        };

        previous?.addEventListener('click', () => { scrollToCard(activeIndex - 1); restartAutoplay(); });
        next?.addEventListener('click', () => { scrollToCard(activeIndex + 1); restartAutoplay(); });
        dots.forEach((dot, index) => dot.addEventListener('click', () => { scrollToCard(index); restartAutoplay(); }));
        rail.addEventListener('scroll', () => {
          window.cancelAnimationFrame(scrollFrame);
          scrollFrame = window.requestAnimationFrame(() => syncControls(Math.round(rail.scrollLeft / cardStep())));
        }, { passive: true });
        window.addEventListener('resize', setMediaHeight, { passive: true });

        rail.scrollLeft = 0;
        setMediaHeight();
        syncControls(0);
        restartAutoplay();
      });
    };

    initializeBlogCarousels();
    document.addEventListener('shopify:section:load', (event) => initializeBlogCarousels(event.target));
    const blogSlider = document.querySelector('.clevenne-blog__rail');

    const initializeBenefitsCarousels = (root = document) => {
      root.querySelectorAll('[data-clevenne-benefits]:not([data-clevenne-benefits-bound])').forEach((section) => {
        const rail = section.querySelector('[data-clevenne-benefits-rail]');
        const items = [...section.querySelectorAll('[data-clevenne-benefit]')];
        const dots = [...section.querySelectorAll('[data-clevenne-benefits-dot]')];
        if (!rail || items.length < 2) return;

        section.setAttribute('data-clevenne-benefits-bound', '');
        const mobile = window.matchMedia('(max-width: 989px)');
        let activeIndex = 0;
        let scrollFrame;

        const fitActiveItem = () => {
          if (!mobile.matches) {
            rail.style.removeProperty('height');
            return;
          }
          rail.style.height = `${Math.ceil(items[activeIndex].getBoundingClientRect().height)}px`;
        };
        const sync = (index) => {
          activeIndex = Math.max(0, Math.min(items.length - 1, index));
          items.forEach((item, itemIndex) => item.classList.toggle('is-selected', itemIndex === activeIndex));
          dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', String(dotIndex === activeIndex)));
          fitActiveItem();
        };
        const goTo = (index) => {
          rail.scrollTo({ left: items[index].offsetLeft - items[0].offsetLeft, behavior: 'smooth' });
          sync(index);
        };

        dots.forEach((dot, index) => dot.addEventListener('click', () => goTo(index)));
        rail.addEventListener('scroll', () => {
          window.cancelAnimationFrame(scrollFrame);
          scrollFrame = window.requestAnimationFrame(() => sync(Math.round(rail.scrollLeft / rail.clientWidth)));
        }, { passive: true });
        mobile.addEventListener?.('change', () => { rail.scrollLeft = 0; sync(0); });
        window.addEventListener('resize', fitActiveItem, { passive: true });

        rail.scrollLeft = 0;
        sync(0);
      });
    };

    initializeBenefitsCarousels();
    document.addEventListener('shopify:section:load', (event) => initializeBenefitsCarousels(event.target));

    const initializeCategoryCarousels = (root = document) => {
      root.querySelectorAll('[data-clevenne-categories]:not([data-clevenne-categories-bound])').forEach((section) => {
        const rail = section.querySelector('[data-clevenne-categories-rail]');
        const previous = section.querySelector('[data-clevenne-categories-prev]');
        const next = section.querySelector('[data-clevenne-categories-next]');
        if (!rail || !previous || !next) return;

        section.setAttribute('data-clevenne-categories-bound', '');
        let scrollFrame;
        const edgeTolerance = 12;
        const syncControls = () => {
          const maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
          previous.disabled = rail.scrollLeft <= edgeTolerance;
          next.disabled = rail.scrollLeft >= maximum - edgeTolerance;
        };
        const move = (direction) => {
          const cards = Array.from(rail.querySelectorAll('[data-clevenne-category]'));
          const railCenter = rail.scrollLeft + rail.clientWidth / 2;
          const currentIndex = cards.reduce((nearestIndex, card, index) => {
            const nearest = cards[nearestIndex];
            const cardCenter = card.offsetLeft + card.offsetWidth / 2;
            const nearestCenter = nearest.offsetLeft + nearest.offsetWidth / 2;
            return Math.abs(cardCenter - railCenter) < Math.abs(nearestCenter - railCenter) ? index : nearestIndex;
          }, 0);
          const nextIndex = Math.max(0, Math.min(cards.length - 1, currentIndex + direction));
          const target = cards[nextIndex];
          if (!target) return;
          const maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
          const destination = Math.max(0, Math.min(maximum, target.offsetLeft + target.offsetWidth / 2 - rail.clientWidth / 2));
          rail.scrollTo({ left: destination, behavior: 'smooth' });
        };

        previous.addEventListener('click', () => move(-1));
        next.addEventListener('click', () => move(1));
        rail.addEventListener('scroll', () => {
          window.cancelAnimationFrame(scrollFrame);
          scrollFrame = window.requestAnimationFrame(syncControls);
        }, { passive: true });
        window.addEventListener('resize', syncControls, { passive: true });

        rail.scrollLeft = 0;
        syncControls();
      });
    };

    initializeCategoryCarousels();
    document.addEventListener('shopify:section:load', (event) => initializeCategoryCarousels(event.target));

    const footerMobile = window.matchMedia('(max-width: 989px)');
    const syncFooterMenus = () => {
      document.querySelectorAll('.clevenne-footer__menu').forEach((menu) => {
        if (footerMobile.matches) menu.removeAttribute('open');
        else menu.setAttribute('open', '');
      });
    };
    syncFooterMenus();
    footerMobile.addEventListener?.('change', syncFooterMenus);

    if (footerMobile.matches) {
      const categorySlider = document.querySelector('.clevenne-categories__grid');
      const benefitsSlider = document.querySelector('.clevenne-benefits__grid');
      const alignMobileRails = () => {
        if (categorySlider) categorySlider.scrollLeft = 0;
        if (benefitsSlider) benefitsSlider.scrollLeft = 0;
        if (blogSlider) blogSlider.scrollLeft = 0;
      };
      alignMobileRails();
      window.setTimeout(alignMobileRails, 350);
    }

    const productHandle = window.clevenneProductHandle || window.meta?.product?.handle;
    if (productHandle) {
      const viewed = JSON.parse(localStorage.getItem('clevenne-recently-viewed') || '[]');
      localStorage.setItem('clevenne-recently-viewed', JSON.stringify([productHandle, ...viewed.filter((handle) => handle !== productHandle)].slice(0, 12)));
    }

    document.querySelectorAll('[data-clevenne-recently-viewed]').forEach(async (section) => {
      const handles = JSON.parse(localStorage.getItem('clevenne-recently-viewed') || '[]').filter((handle) => handle !== productHandle).slice(0, Number(section.dataset.limit || 4));
      if (!handles.length) return;
      const products = (await Promise.all(handles.map((handle) => fetch(`${Shopify.routes.root}products/${handle}.js`).then((response) => response.ok ? response.json() : null).catch(() => null)))).filter(Boolean);
      if (!products.length) return;
      const currency = Shopify.currency?.active || 'GBP';
      const locale = document.documentElement.lang || Shopify.locale || 'en-GB';
      const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
      const escapeHtml = (value) => String(value || '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]));
      section.querySelector('ul').innerHTML = products.map((product) => {
        const image = product.featured_image || product.images?.[0] || '';
        const price = formatter.format((product.price || 0) / 100);
        const compareAt = Number(product.compare_at_price || 0);
        const onSale = compareAt > Number(product.price || 0);
        const discount = onSale ? Math.round((1 - Number(product.price) / compareAt) * 100) : 0;
        const compareAtPrice = onSale ? formatter.format(compareAt / 100) : '';
        return `<li class="grid__item"><a class="clevenne-recent-card" href="${product.url}"><div class="clevenne-recent-card__media"><img src="${image}" alt="${escapeHtml(product.title)}" loading="lazy">${onSale ? `<span class="clevenne-recent-card__badge" aria-label="-${discount}%">-${discount}%</span>` : ''}</div><div class="clevenne-recent-card__information"><span class="clevenne-recent-card__vendor">${escapeHtml(product.vendor || 'Clevenne')}</span><h3 class="clevenne-recent-card__title">${escapeHtml(product.title)}</h3><div class="clevenne-recent-card__price${onSale ? ' is-sale' : ''}"><span>${price}</span>${onSale ? `<s>${compareAtPrice}</s>` : ''}</div></div></a></li>`;
      }).join('');
      section.hidden = false;
    });

    document.querySelectorAll('[data-clevenne-hero]').forEach((hero) => {
      const videos = [...hero.querySelectorAll('video')];
      const toggle = hero.querySelector('[data-clevenne-sound]');
      const pause = hero.querySelector('[data-clevenne-pause]');
      const mobileMedia = window.matchMedia('(max-width: 989px)');
      const getActiveVideo = () => {
        const device = mobileMedia.matches ? 'mobile' : 'desktop';
        return hero.querySelector(`[data-clevenne-hero-media="${device}"] video`) || (hero.querySelector('[data-clevenne-hero-media]') ? null : hero.querySelector('video'));
      };
      const mutedIcon = toggle?.querySelector('.clevenne-hero__icon--muted');
      const soundIcon = toggle?.querySelector('.clevenne-hero__icon--sound');
      const soundLabel = toggle?.querySelector('.visually-hidden');
      const pauseIcon = pause?.querySelector('.clevenne-hero__icon--pause');
      const playIcon = pause?.querySelector('.clevenne-hero__icon--play');
      const syncSound = () => {
        const video = getActiveVideo();
        if (toggle) {
          toggle.hidden = !video;
          if (!video) return;
          const label = video.muted ? text('enableSound', 'Enable sound') : text('muteSound', 'Mute sound');
          toggle.setAttribute('aria-pressed', String(!video.muted));
          toggle.setAttribute('aria-label', label);
          if (soundLabel) soundLabel.textContent = label;
          if (mutedIcon) mutedIcon.toggleAttribute('hidden', !video.muted);
          if (soundIcon) soundIcon.toggleAttribute('hidden', video.muted);
        }
      };
      const syncPlayback = () => {
        const video = getActiveVideo();
        if (pause) {
          pause.hidden = !video;
          if (!video) return;
          pause.setAttribute('aria-pressed', String(video.paused));
          pause.setAttribute('aria-label', video.paused ? text('playVideo', 'Play video') : text('pauseVideo', 'Pause video'));
          if (pauseIcon) pauseIcon.toggleAttribute('hidden', video.paused);
          if (playIcon) playIcon.toggleAttribute('hidden', !video.paused);
        }
      };
      const syncActiveMedia = () => {
        const activeVideo = getActiveVideo();
        videos.forEach((video) => {
          if (video !== activeVideo) video.pause();
        });
        if (activeVideo?.paused) activeVideo.play().catch(() => {});
        syncSound();
        syncPlayback();
      };
      if (toggle) {
        toggle.addEventListener('click', () => {
          const video = getActiveVideo();
          if (!video) return;
          video.muted = !video.muted;
          syncSound();
        });
      }
      if (pause) {
        pause.addEventListener('click', () => {
          const video = getActiveVideo();
          if (!video) return;
          if (video.paused) video.play().catch(() => {}); else video.pause();
          syncPlayback();
        });
      }
      videos.forEach((video) => {
        video.addEventListener('volumechange', syncSound);
        video.addEventListener('play', syncPlayback);
        video.addEventListener('pause', syncPlayback);
      });
      mobileMedia.addEventListener?.('change', syncActiveMedia);
      syncActiveMedia();
      hero.querySelector('[data-clevenne-next]')?.addEventListener('click', (event) => {
        const section = hero.closest('.shopify-section');
        if (section?.nextElementSibling) {
          event.preventDefault();
          section.nextElementSibling.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });

    const initializeProductPages = (root = document) => {
      root.querySelectorAll('media-gallery:not([data-clevenne-product-gallery-bound])').forEach((gallery) => {
        const rail = gallery.querySelector('.product__media-list');
        const items = [...gallery.querySelectorAll('.product__media-item')];
        const dots = [...gallery.querySelectorAll('[data-clevenne-product-gallery-dot]')];
        const previous = gallery.querySelector('[data-clevenne-product-gallery-previous]');
        const next = gallery.querySelector('[data-clevenne-product-gallery-next]');
        if (!rail || items.length < 2 || dots.length < 2) return;

        gallery.setAttribute('data-clevenne-product-gallery-bound', '');
        let frame;
        let currentIndex = 0;
        let dragStartX = null;
        let dragStartY = null;
        let dragLastX = null;
        let dragAxis = null;
        let activePointerId = null;
        let suppressMediaClick = false;
        const step = () => items[1].offsetLeft - items[0].offsetLeft;
        const setActive = (index) => {
          const active = Math.max(0, Math.min(items.length - 1, index));
          currentIndex = active;
          dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', String(dotIndex === active)));
          if (previous) {
            previous.hidden = active === 0;
            previous.setAttribute('aria-hidden', String(active === 0));
          }
        };
        const goTo = (index) => {
          const destination = index >= items.length ? 0 : Math.max(0, index);
          const left = items[destination].offsetLeft;
          rail.style.transform = `translate3d(${-left}px, 0, 0)`;
          setActive(destination);
        };

        dots.forEach((dot, index) => dot.addEventListener('click', () => goTo(index)));
        previous?.addEventListener('click', () => goTo(currentIndex - 1));
        next?.addEventListener('click', () => goTo(currentIndex + 1));
        const resetDrag = () => {
          dragStartX = null;
          dragStartY = null;
          dragLastX = null;
          dragAxis = null;
          activePointerId = null;
          rail.style.transition = '';
          rail.style.cursor = '';
        };
        rail.addEventListener('pointerdown', (event) => {
          if (!window.matchMedia('(max-width: 989px)').matches || !event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
          activePointerId = event.pointerId;
          dragStartX = event.clientX;
          dragStartY = event.clientY;
          dragLastX = event.clientX;
          dragAxis = null;
          rail.setPointerCapture?.(event.pointerId);
        }, true);
        rail.addEventListener('pointermove', (event) => {
          if (activePointerId !== event.pointerId || dragStartX === null || dragStartY === null) return;
          const distanceX = event.clientX - dragStartX;
          const distanceY = event.clientY - dragStartY;
          dragLastX = event.clientX;

          if (dragAxis === null && (Math.abs(distanceX) > 7 || Math.abs(distanceY) > 7)) {
            dragAxis = Math.abs(distanceX) > Math.abs(distanceY) ? 'horizontal' : 'vertical';
          }
          if (dragAxis !== 'horizontal') return;

          event.preventDefault();
          const isPastStart = currentIndex === 0 && distanceX > 0;
          const isPastEnd = currentIndex === items.length - 1 && distanceX < 0;
          const dragDistance = (isPastStart || isPastEnd) ? distanceX * .28 : distanceX;
          rail.style.transition = 'none';
          rail.style.cursor = 'grabbing';
          rail.style.transform = `translate3d(${dragDistance - items[currentIndex].offsetLeft}px, 0, 0)`;
        }, { passive: false, capture: true });
        rail.addEventListener('pointerup', (event) => {
          if (activePointerId !== event.pointerId || dragStartX === null) return;
          const distanceX = (event.clientX || dragLastX || dragStartX) - dragStartX;
          const wasHorizontalSwipe = dragAxis === 'horizontal';
          if (rail.hasPointerCapture?.(event.pointerId)) rail.releasePointerCapture(event.pointerId);
          resetDrag();

          if (!wasHorizontalSwipe) return;
          event.preventDefault();
          suppressMediaClick = true;
          window.setTimeout(() => { suppressMediaClick = false; }, 400);
          const threshold = Math.min(64, Math.max(36, rail.clientWidth * .12));
          if (Math.abs(distanceX) >= threshold) {
            goTo(currentIndex + (distanceX < 0 ? 1 : -1));
          } else {
            goTo(currentIndex);
          }
        }, { passive: false, capture: true });
        rail.addEventListener('pointercancel', (event) => {
          if (activePointerId !== event.pointerId) return;
          resetDrag();
          goTo(currentIndex);
        }, true);
        rail.addEventListener('click', (event) => {
          if (!suppressMediaClick) return;
          event.preventDefault();
          event.stopPropagation();
        }, true);
        rail.addEventListener('scroll', () => {
          window.cancelAnimationFrame(frame);
          frame = window.requestAnimationFrame(() => setActive(Math.round(rail.scrollLeft / step())));
        }, { passive: true });
        setActive(0);
      });

      root.querySelectorAll('media-gallery:not([data-clevenne-desktop-thumbnails-bound])').forEach((gallery) => {
        const buttons = [...gallery.querySelectorAll('[data-clevenne-desktop-thumbnail]')];
        if (!buttons.length) return;
        gallery.setAttribute('data-clevenne-desktop-thumbnails-bound', '');

        const selectThumbnail = (mediaId) => {
          buttons.forEach((button) => button.setAttribute('aria-current', String(button.dataset.clevenneDesktopThumbnail === mediaId)));
        };

        const mediaItems = [...gallery.querySelectorAll('.product__media-item')];
        const getHeaderOffset = () => {
          const header = document.querySelector('.section-header');
          return Math.max(0, header?.getBoundingClientRect().height || 0) + 20;
        };
        const getViewportAnchor = () => {
          const headerOffset = getHeaderOffset();
          return headerOffset + Math.max(0, window.innerHeight - headerOffset) * 0.34;
        };
        const syncThumbnailToScroll = () => {
          if (!window.matchMedia('(min-width: 990px)').matches || !mediaItems.length) return;

          const anchor = getViewportAnchor();
          const activeItem = mediaItems.find((item) => {
            const rect = item.getBoundingClientRect();
            return rect.top <= anchor && rect.bottom > anchor;
          }) || mediaItems.reduce((closest, item) => {
            const rect = item.getBoundingClientRect();
            const distance = Math.abs((rect.top + rect.bottom) / 2 - anchor);
            return !closest || distance < closest.distance ? { item, distance } : closest;
          }, null)?.item;

          if (activeItem?.dataset.mediaId) selectThumbnail(activeItem.dataset.mediaId);
        };

        let scrollFrame;
        let lockedMediaId = null;
        let unlockTimer;
        const releaseThumbnailLock = () => {
          lockedMediaId = null;
          window.cancelAnimationFrame(scrollFrame);
          scrollFrame = window.requestAnimationFrame(syncThumbnailToScroll);
        };
        const scheduleThumbnailUnlock = () => {
          window.clearTimeout(unlockTimer);
          unlockTimer = window.setTimeout(releaseThumbnailLock, 160);
        };
        const requestThumbnailSync = () => {
          if (lockedMediaId) {
            selectThumbnail(lockedMediaId);
            scheduleThumbnailUnlock();
            return;
          }
          window.cancelAnimationFrame(scrollFrame);
          scrollFrame = window.requestAnimationFrame(syncThumbnailToScroll);
        };

        buttons.forEach((button) => {
          button.addEventListener('click', () => {
            const mediaId = button.dataset.clevenneDesktopThumbnail;
            const item = gallery.querySelector(`[data-media-id="${mediaId}"]`);
            if (!item) return;
            lockedMediaId = mediaId;
            selectThumbnail(mediaId);
            if (typeof gallery.setActiveMedia === 'function') {
              gallery.setActiveMedia(mediaId, false);
            }
            scheduleThumbnailUnlock();
          });
        });

        window.addEventListener('scroll', requestThumbnailSync, { passive: true });
        window.addEventListener('resize', requestThumbnailSync, { passive: true });
        requestThumbnailSync();
      });

      root.querySelectorAll('[data-clevenne-product-quick-links]:not([data-clevenne-product-quick-links-bound])').forEach((links) => {
        links.setAttribute('data-clevenne-product-quick-links-bound', '');
        links.querySelectorAll('[data-clevenne-accordion-target]').forEach((button) => {
          button.addEventListener('click', () => {
            const key = button.dataset.clevenneAccordionTarget;
            const preferredClass = window.matchMedia('(min-width: 990px)').matches
              ? '.clevenne-product-accordions--desktop'
              : '.clevenne-product-accordions--mobile';
            const accordions = document.querySelector(preferredClass);
            if (!accordions) return;
            if (key === 'more') {
              accordions.scrollIntoView({ behavior: 'smooth', block: 'start' });
              return;
            }
            const details = accordions.querySelector(`[data-clevenne-product-accordion="${key}"]`);
            if (!details) return;
            if (!details.open) details.querySelector('summary')?.click();
            details.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        });
      });

      root.querySelectorAll('.clevenne-product-accordions details:not([data-clevenne-accordion-bound])').forEach((details) => {
        const summary = details.querySelector('summary');
        const panel = details.querySelector('.clevenne-product-accordions__panel');
        if (!summary || !panel) return;
        details.setAttribute('data-clevenne-accordion-bound', '');
        const syncState = () => {
          summary.setAttribute('aria-expanded', String(details.open));
          panel.setAttribute('aria-hidden', String(!details.open));
          if (details.open) panel.hidden = false;
          else if (!details.classList.contains('is-closing')) panel.hidden = true;
        };
        summary.addEventListener('click', (event) => {
          event.preventDefault();
          if (details.classList.contains('is-closing')) return;
          if (!details.open) {
            panel.hidden = false;
            panel.getBoundingClientRect();
            details.open = true;
            return;
          }
          details.classList.add('is-closing');
          summary.setAttribute('aria-expanded', 'false');
          panel.setAttribute('aria-hidden', 'true');
          window.setTimeout(() => {
            details.open = false;
            details.classList.remove('is-closing');
            panel.hidden = true;
            syncState();
          }, 360);
        });
        details.addEventListener('toggle', syncState);
        syncState();
      });

      root.querySelectorAll('[data-clevenne-sticky-atc]:not([data-clevenne-sticky-atc-bound])').forEach((sticky) => {
        const section = sticky.closest('product-info');
        const formButton = section?.querySelector('.product-form__submit');
        if (!formButton) return;
        sticky.setAttribute('data-clevenne-sticky-atc-bound', '');
        const stickyButton = sticky.querySelector('.clevenne-sticky-atc__submit');
        const stickyVariant = sticky.querySelector('[data-clevenne-sticky-variant]');
        const quantityInput = sticky.querySelector('[data-clevenne-sticky-quantity-input]');
        const quantityMinus = sticky.querySelector('[data-clevenne-sticky-minus]');
        const quantityPlus = sticky.querySelector('[data-clevenne-sticky-plus]');
        const syncPrice = () => {
          const livePrice = section.querySelector('.price--on-sale');
          const sale = livePrice?.querySelector('.price-item--sale:not([hidden])');
          const regular = [...(livePrice?.querySelectorAll('.price-item--regular') || [])]
            .find((item) => item.offsetParent !== null && item.textContent.trim() !== sale?.textContent.trim());
          const stickySale = sticky.querySelector('.clevenne-sticky-atc__prices strong');
          const stickyRegular = sticky.querySelector('.clevenne-sticky-atc__prices s');
          if (sale && stickySale && stickySale.textContent.trim() !== sale.textContent.trim()) {
            stickySale.textContent = sale.textContent.trim();
          }
          if (stickyRegular) {
            stickyRegular.hidden = !regular;
            if (regular && stickyRegular.textContent.trim() !== regular.textContent.trim()) {
              stickyRegular.textContent = regular.textContent.trim();
            }
          }
        };
        const syncControls = () => {
          const selectedVariant = section.querySelector('variant-selects [data-selected-variant]');
          if (stickyVariant && selectedVariant?.textContent) {
            try {
              const variant = JSON.parse(selectedVariant.textContent);
              if (variant?.id) stickyVariant.value = String(variant.id);
            } catch (_error) {
              // The main product form remains authoritative if Shopify is still replacing its variant JSON.
            }
          }
          if (stickyButton) {
            stickyButton.disabled = formButton.disabled;
            const sourceLabel = formButton.querySelector('span')?.textContent.trim();
            const nextLabel = sourceLabel || 'Add to cart';
            if (stickyButton.textContent.trim() !== nextLabel) stickyButton.textContent = nextLabel;
          }
          if (quantityInput && quantityMinus) {
            quantityMinus.disabled = Number(quantityInput.value) <= Number(quantityInput.min || 1);
          }
        };
        const sync = () => {
          const visible = formButton.getBoundingClientRect().bottom < 0;
          sticky.classList.toggle('is-visible', visible);
          sticky.setAttribute('aria-hidden', String(!visible));
        };
        stickyVariant?.addEventListener('change', () => {
          const selected = stickyVariant.selectedOptions[0];
          if (!selected?.dataset.options) return;
          let values;
          try {
            values = JSON.parse(selected.dataset.options);
          } catch (_error) {
            return;
          }
          const variantSelects = section.querySelector('variant-selects');
          if (!variantSelects) return;
          const groups = [...variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown')];
          let changedControl = null;
          groups.forEach((group, index) => {
            const value = values[index];
            if (value == null) return;
            const select = group.querySelector('select');
            if (select) {
              const option = [...select.options].find((item) => item.value === value);
              if (!option) return;
              [...select.options].forEach((item) => item.removeAttribute('selected'));
              select.value = value;
              option.setAttribute('selected', 'selected');
              changedControl = select;
              return;
            }
            const radio = [...group.querySelectorAll('input[type="radio"]')].find((item) => item.value === value);
            if (!radio) return;
            radio.checked = true;
            changedControl = radio;
          });
          changedControl?.dispatchEvent(new Event('change', { bubbles: true }));
        });
        quantityMinus?.addEventListener('click', () => {
          if (!quantityInput) return;
          quantityInput.stepDown();
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          syncControls();
        });
        quantityPlus?.addEventListener('click', () => {
          if (!quantityInput) return;
          quantityInput.stepUp();
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          syncControls();
        });
        quantityInput?.addEventListener('change', syncControls);
        stickyButton?.addEventListener('click', () => {
          const variantInput = stickyButton.form?.querySelector('.product-variant-id, input[name="id"]');
          if (!variantInput) return;
          let selectedId = stickyVariant?.value;
          if (!selectedId) {
            const selectedVariant = section.querySelector('variant-selects [data-selected-variant]');
            try {
              selectedId = JSON.parse(selectedVariant?.textContent || '{}')?.id;
            } catch (_error) {
              selectedId = null;
            }
          }
          if (selectedId) variantInput.value = String(selectedId);
        });
        document.addEventListener('scroll', sync, { passive: true });
        window.addEventListener('resize', sync, { passive: true });
        document.addEventListener('variant:change', () => window.setTimeout(() => {
          syncPrice();
          syncControls();
        }, 100));
        section.addEventListener('change', (event) => {
          if (event.target.closest('variant-selects')) window.setTimeout(() => {
            syncPrice();
            syncControls();
          }, 250);
        });
        const controlsObserver = new MutationObserver(() => {
          syncPrice();
          syncControls();
        });
        const controlsRoot = section.querySelector('.product__info-container');
        if (controlsRoot) controlsObserver.observe(controlsRoot, { childList: true, subtree: true, characterData: true });
        syncPrice();
        syncControls();
        sync();
      });

      root.querySelectorAll('[data-clevenne-product-description]:not([data-clevenne-product-description-bound])').forEach((description) => {
        const content = description.querySelector('[data-clevenne-product-description-content]');
        const toggle = description.querySelector('[data-clevenne-product-description-toggle]');
        if (!content || !toggle) return;
        description.setAttribute('data-clevenne-product-description-bound', '');
        toggle.addEventListener('click', () => {
          const expanded = description.classList.toggle('is-expanded');
          toggle.setAttribute('aria-expanded', String(expanded));
          toggle.textContent = expanded ? text('readLess', 'Read less') : text('readMore', 'Read more');
        });
      });

      root.querySelectorAll('.clevenne-custom-option--image_round, .clevenne-custom-option--image_square').forEach((option) => {
        const media = option.querySelectorAll('.clevenne-custom-option__media');
        const firstGalleryImage = root.querySelector('.product__media-list .product__media-item img');
        if (media.length !== 1 || !firstGalleryImage) return;
        const syncImage = () => {
          const source = firstGalleryImage.currentSrc || firstGalleryImage.src;
          if (source) media[0].style.backgroundImage = `url("${source}")`;
        };
        if (firstGalleryImage.complete) syncImage(); else firstGalleryImage.addEventListener('load', syncImage, { once: true });
      });
    };

    const initializeProductRails = (root = document) => {
      root.querySelectorAll('[data-clevenne-product-rail]').forEach((section) => {
        const bind = () => {
          const track = section.querySelector('[data-clevenne-product-rail-track]');
          const cards = track ? [...track.querySelectorAll('[data-clevenne-product-rail-card]:not([data-clevenne-clone])')] : [];
          if (!track || cards.length < 2 || track.hasAttribute('data-clevenne-product-rail-bound')) return false;
          track.setAttribute('data-clevenne-product-rail-bound', '');
          const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
          const distance = cards.reduce((total, card) => total + card.getBoundingClientRect().width, 0) + gap * cards.length;
          cards.forEach((card) => {
            const clone = card.cloneNode(true);
            clone.setAttribute('data-clevenne-clone', '');
            clone.setAttribute('aria-hidden', 'true');
            clone.querySelectorAll('a,button,input').forEach((item) => item.setAttribute('tabindex', '-1'));
            track.appendChild(clone);
          });
          track.style.setProperty('--clevenne-product-rail-distance', `${distance}px`);
          track.classList.add('is-marquee');
          return true;
        };
        if (bind()) return;
        const observer = new MutationObserver(() => { if (bind()) observer.disconnect(); });
        observer.observe(section, { childList: true, subtree: true });
      });
    };

    initializeProductPages();
    initializeProductRails();
    const syncClevenneStickyHeaderHeight = () => {
      const header = document.querySelector('.section-header');
      if (header) document.documentElement.style.setProperty('--clevenne-sticky-header-height', `${header.offsetHeight}px`);
    };
    syncClevenneStickyHeaderHeight();
    window.addEventListener('resize', syncClevenneStickyHeaderHeight);
    document.addEventListener('shopify:section:load', (event) => {
      initializeProductPages(event.target);
      initializeProductRails(event.target);
    });
    document.addEventListener('variant:change', () => window.setTimeout(() => initializeProductPages(), 100));

    document.querySelectorAll('[data-clevenne-popup]').forEach((popup) => {
      const key = `clevenne-popup-${popup.dataset.clevennePopup}`;
      const delay = Number(popup.dataset.delay || 7) * 1000;
      const close = () => {
        popup.removeAttribute('open');
        sessionStorage.setItem(key, 'closed');
      };
      popup.querySelectorAll('[data-popup-close]').forEach((button) => button.addEventListener('click', close));
      popup.addEventListener('click', (event) => { if (event.target === popup) close(); });
      if (!Shopify.designMode && !sessionStorage.getItem(key)) {
        window.setTimeout(() => popup.setAttribute('open', ''), delay);
      }
      if (Shopify.designMode) popup.setAttribute('open', '');
    });

    document.querySelectorAll('[id^="Details-filter.v."], [id^="Details-Mobile-filter.v."]').forEach((facet) => {
      facet.removeAttribute('open');
    });

    const initializeMobileCollectionToolbar = (root = document) => {
      root.querySelectorAll('[id*="__product-grid"] [data-clevenne-mobile-sort-trigger]:not([data-clevenne-toolbar-bound])').forEach((trigger) => {
        trigger.setAttribute('data-clevenne-toolbar-bound', '');
        const container = trigger.closest('.facets-container');
        const select = container?.querySelector('#SortBy-mobile');
        if (!select) return;
        const filterHeading = container.querySelector('.mobile-facets__heading.medium-hide.large-up-hide');
        if (filterHeading) filterHeading.textContent = text('filters', 'Filters');

        const sheet = document.createElement('div');
        sheet.className = 'clevenne-sort-sheet';
        sheet.setAttribute('role', 'dialog');
        sheet.setAttribute('aria-modal', 'true');
        sheet.setAttribute('aria-label', trigger.textContent.trim());
        sheet.innerHTML = `
          <button type="button" class="clevenne-sort-sheet__backdrop" aria-label="${text('close', 'Close')}"></button>
          <div class="clevenne-sort-sheet__panel">
            <div class="clevenne-sort-sheet__header">
              <p class="clevenne-sort-sheet__title">${trigger.textContent.trim()}</p>
              <button type="button" class="clevenne-sort-sheet__close" aria-label="${text('close', 'Close')}">
                <svg aria-hidden="true" viewBox="0 0 16 16"><path d="M2 2L14 14M14 2L2 14" fill="none" stroke="currentColor" stroke-width="1"/></svg>
              </button>
            </div>
            <div class="clevenne-sort-sheet__options"></div>
          </div>`;
        document.body.appendChild(sheet);

        const options = sheet.querySelector('.clevenne-sort-sheet__options');
        [...select.options].forEach((option) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'clevenne-sort-sheet__option';
          button.textContent = option.textContent.trim();
          button.dataset.value = option.value;
          button.setAttribute('aria-current', option.selected ? 'true' : 'false');
          options.appendChild(button);
        });

        const close = () => {
          sheet.removeAttribute('open');
          trigger.setAttribute('aria-expanded', 'false');
          document.documentElement.classList.remove('clevenne-sort-sheet-open');
          trigger.focus({ preventScroll: true });
        };
        const open = () => {
          sheet.setAttribute('open', '');
          trigger.setAttribute('aria-expanded', 'true');
          document.documentElement.classList.add('clevenne-sort-sheet-open');
        };

        trigger.addEventListener('click', open);
        sheet.querySelectorAll('.clevenne-sort-sheet__backdrop, .clevenne-sort-sheet__close').forEach((button) => button.addEventListener('click', close));
        sheet.addEventListener('click', (event) => {
          const option = event.target.closest('.clevenne-sort-sheet__option');
          if (!option) return;
          select.value = option.dataset.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          close();
        });
        sheet.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') close();
        });
      });
    };

    initializeMobileCollectionToolbar();
    document.addEventListener('shopify:section:load', (event) => initializeMobileCollectionToolbar(event.target));
  });
})();
